import React, { useState } from 'react'
import { Box, Typography, IconButton, Select, MenuItem } from '@mui/material'
import { Edit as EditIcon } from '@mui/icons-material'
import { useMutation, gql } from '@apollo/client'
import { Program } from 'src/legacy/models/firebase'
import { getThemeLabel } from './programThemeLabel'

const UPDATE_PATIENT_PROGRAM_START_DATE = gql`
  mutation UpdatePatientProgramStartDate($uid: ID!, $programStartDate: Date!, $isPostProgram: Boolean!) {
    updatePatientProgramStartDate(uid: $uid, programStartDate: $programStartDate, isPostProgram: $isPostProgram) {
      uid
      enrollment {
        programStartDate
      }
    }
  }
`

type Props = {
  uid: string
  lastWeekNbr: number
  weekNbr: number | null
  programThemes: Program['weeks']
}

const calculateProgramStartDate = (selectedWeek: number): string => {
  const today = new Date()

  const dayOfWeek = today.getDay()
  const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1 // Sunday = 0, Monday = 1
  const currentMonday = new Date(today)
  currentMonday.setDate(today.getDate() - daysToMonday)

  // Add 1 week, then subtract selectedWeek weeks from current Monday
  const programStartDate = new Date(currentMonday)
  programStartDate.setDate(currentMonday.getDate() + (1 - selectedWeek) * 7)

  return programStartDate.toISOString().split('T')[0]
}

// For development purposes only
export default function ProgramThemeEditable({ uid, lastWeekNbr, weekNbr, programThemes }: Props) {
  const [isEditing, setIsEditing] = useState(false)
  const [selectedWeek, setSelectedWeek] = useState<number | null>(null)
  const [updateProgramStartDate] = useMutation(UPDATE_PATIENT_PROGRAM_START_DATE)

  const weekOptions = Array.from({ length: lastWeekNbr + 2 }, (_, i) => i)
  const currentWeek = selectedWeek ?? weekNbr ?? 0
  const programTheme = getThemeLabel(weekNbr, lastWeekNbr, programThemes)

  const handleEditClick = () => {
    setSelectedWeek(weekNbr)
    setIsEditing(true)
  }

  const handleWeekChange = async (event: { target: { value: unknown } }) => {
    const newWeek = event.target.value as number
    setSelectedWeek(newWeek)

    const calculatedStartDate = calculateProgramStartDate(newWeek)
    const isPostProgram = newWeek > lastWeekNbr

    await updateProgramStartDate({
      variables: {
        uid,
        programStartDate: calculatedStartDate,
        isPostProgram,
      },
    })
  }

  return isEditing ? (
    <Box display="flex" alignItems="center" gap={1}>
      <Select value={currentWeek} onChange={handleWeekChange} size="small" autoFocus onBlur={() => setIsEditing(false)}>
        {weekOptions.map((week) => (
          <MenuItem key={week} value={week}>
            {getThemeLabel(week, lastWeekNbr, programThemes)}
          </MenuItem>
        ))}
      </Select>
    </Box>
  ) : (
    <Box display="flex" alignItems="center" gap={1}>
      <Typography variant="h3">{programTheme}</Typography>
      <IconButton onClick={handleEditClick} size="small" color="primary">
        <EditIcon fontSize="small" />
      </IconButton>
    </Box>
  )
}
